import React, { useState, useEffect, useRef } from "react";
import { McButton, McTextarea } from "@maersk-global/mds-react-wrapper/components-core";
import "./ApprovalWorkflow.css";

/**
 * Approval Panel Component (Simplified - Single Leadership Review)
 * Displays approval panel with comment box and approve/reject buttons
 * First approver wins (ED or TLT)
 */
const ApprovalPanel = ({
  approvers,
  currentStage,
  status,
  onApprove,
  onReject,
  canApprove,
  loading,
  routingInfo,
  leadershipReviewCompleted,
  leadershipReviewApprovedBy,
  leadershipReviewApprovedAt,
  leadershipReviewApproverType,
  leadershipReviewComment,
}) => {
  const [comment, setComment] = useState("");
  const [showRejectError, setShowRejectError] = useState(false);
  const wasLoadingRef = useRef(false);
  const textareaRef = useRef(null);

  // Clear comment after operation completes (loading goes from true to false)
  useEffect(() => {
    if (wasLoadingRef.current && !loading) {
      console.log("[ApprovalPanel] Operation completed, clearing comment");
      setComment("");
      if (textareaRef.current) {
        textareaRef.current.value = "";
      }
    }
    wasLoadingRef.current = loading;
  }, [loading]);

  const handleApprove = () => {
    const currentComment = textareaRef.current?.value || "";
    console.log("[ApprovalPanel] Approving with comment:", currentComment);
    onApprove(currentComment);
    setShowRejectError(false);
  };

  const handleReject = () => {
    const currentComment = textareaRef.current?.value || "";
    console.log("[ApprovalPanel] Rejecting with comment:", currentComment);
    
    if (!currentComment || currentComment.trim().length === 0) {
      setShowRejectError(true);
      console.log("[ApprovalPanel] Rejection blocked - comment is required");
      return;
    }
    onReject(currentComment);
    setShowRejectError(false);
  };

  // Check if current stage is approved
  const isLeadershipReviewApproved = currentStage === "LEADERSHIP_REVIEW" && leadershipReviewCompleted;

  const getStageTitle = () => {
    if (isLeadershipReviewApproved) return "LEADERSHIP REVIEW — APPROVED";
    if (currentStage === "LEADERSHIP_REVIEW") return "LEADERSHIP REVIEW — APPROVAL REQUIRED";
    return "APPROVAL REQUIRED";
  };

  const getStatusBadge = () => {
    if (isLeadershipReviewApproved) return "APPROVED";
    if (status === "AWAITING_APPROVAL") return "AWAITING APPROVAL";
    return status;
  };

  // If stage is approved, show approved state
  if (isLeadershipReviewApproved) {
    const approverTypeLabel = leadershipReviewApproverType === "ED" ? "Engineering Director" : "TLT Leadership";

    return (
      <div className="approval-panel approval-panel--approved">
        <div className="approval-panel-header">
          <span className="approval-panel-title">{getStageTitle()}</span>
          <span className="approval-panel-badge approval-panel-badge--success">{getStatusBadge()}</span>
        </div>

        <div className="approval-panel-body">
          <div className="approval-approved-info">
            <div className="approval-approved-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#2c8a4d" opacity="0.1"/>
                <path d="M20 24L22 26L28 20" stroke="#2c8a4d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="approval-approved-details">
              <div className="approval-approved-title">Leadership Review Approved</div>
              <div className="approval-approved-meta">
                Approved by <strong>{leadershipReviewApprovedBy}</strong> ({approverTypeLabel}) on {new Date(leadershipReviewApprovedAt).toLocaleString()}
              </div>
              {leadershipReviewComment && (
                <div className="approval-approved-comment">
                  <strong>Comment:</strong> {leadershipReviewComment}
                </div>
              )}
            </div>
          </div>

          <div className="approval-next-action">
            <p><strong>Status:</strong> Postmortem is now complete and locked.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="approval-panel">
      <div className="approval-panel-header">
        <span className="approval-panel-title">{getStageTitle()}</span>
        <span className="approval-panel-badge">{getStatusBadge()}</span>
      </div>

      <div className="approval-panel-body">
        {/* Approver Info - Show all approvers */}
        <div className="approval-approver-info">
          <div className="approval-approver-details">
            <div className="approval-approver-name">
              Authorized Approvers ({approvers?.length || 0}):
            </div>
            <div className="approval-approver-list">
              {approvers && approvers.length > 0 ? (
                approvers.map((email, idx) => (
                  <div key={idx} className="approval-approver-email">
                    • {email}
                  </div>
                ))
              ) : (
                <div className="approval-approver-email">Loading...</div>
              )}
            </div>
            <div className="approval-approver-note">
              <strong>First Approver Determines Outcome:</strong> Any authorized ED or TLT reviewer can approve this postmortem.
            </div>
          </div>
        </div>

        {/* Routing Info */}
        {routingInfo && (
          <div className="approval-routing-info">
            <p>{routingInfo}</p>
          </div>
        )}

        {/* Comment Box */}
        <div className="approval-comment-section">
          <label className="approval-comment-label">
            REVIEW COMMENT (OPTIONAL FOR APPROVAL, REQUIRED FOR REJECTION)
          </label>
          <McTextarea
            ref={textareaRef}
            onInput={(e) => {
              if (showRejectError) setShowRejectError(false);
            }}
            placeholder="Add context for the audit trail — e.g. confirm root cause and corrective actions before sign-off."
            rows={4}
            disabled={!canApprove || loading}
            label=""
          />
          {showRejectError && (
            <div className="approval-error-message">Comment is required for rejection</div>
          )}
        </div>

        {/* Action Buttons */}
        {canApprove ? (
          <div className="approval-action-buttons">
            <McButton
              appearance="error"
              variant="outlined"
              label="Reject"
              onClick={handleReject}
              disabled={loading}
            />
            <McButton
              appearance="success"
              variant="filled"
              icon="check"
              label="Approve & Complete"
              onClick={handleApprove}
              disabled={loading}
            />
          </div>
        ) : (
          <div className="approval-read-only-notice">
            <p>You do not have permission to approve or reject this postmortem.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalPanel;
